import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  BattleState,
  BattleUnitState,
  TurnOrderEntry,
  HotbarSkill,
  BattleItem,
  VictoryData,
  MaterialAward,
  OpportunityAttack,
  AchievementUnlock,
  DiscoveredRecipe,
  PassiveEvent,
  AIAction,
} from '../types/battle';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import { getCurrentTurnUnit, getComboCallout } from '../utils/battleUtils';
import { FloatingText } from '../components/BattleEffects';
import { Notification } from '../components/NotificationToast';
import { ConfirmOptions } from '../components/ConfirmModal';
import { logger } from '../utils/logger';

const TILE_SIZE = 80;
const gridToPixel = (gridX: number, gridY: number) => ({
  x: gridX * TILE_SIZE + TILE_SIZE / 2,
  y: gridY * TILE_SIZE + TILE_SIZE / 2,
});

interface UseBattleActionsProps {
  battleId: number;
  battle: BattleState | null;
  selectedUnit: BattleUnitState | null;
  selectedSkill: HotbarSkill | null;
  selectedItem: BattleItem | null;
  setBattle: React.Dispatch<React.SetStateAction<BattleState | null>>;
  setSelectedUnit: React.Dispatch<React.SetStateAction<BattleUnitState | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setActionError: React.Dispatch<React.SetStateAction<string | null>>;
  setShowVictoryScreen: React.Dispatch<React.SetStateAction<boolean>>;
  setVictoryData: React.Dispatch<React.SetStateAction<VictoryData>>;
  battleLog: string[];
  setBattleLog: React.Dispatch<React.SetStateAction<string[]>>;
  addToBattleLog: (msg: string) => void;
  addEnhancedBattleLog: (type: string, data: Record<string, unknown>) => void;
  addFloatingText: (text: string, type: FloatingText['type'], x: number, y: number) => void;
  spawnFx?: (skillOrAction: string, from: { x: number; y: number }, to: { x: number; y: number }) => void;
  processPassiveEvents: (
    events: PassiveEvent[],
    units: BattleUnitState[],
    addLog: (type: string, data: Record<string, unknown>) => void
  ) => void;
  processAIActions: (actions: AIAction[], onComplete?: () => void) => void;
  showTurnTransition: (name: string, isPlayer: boolean) => void;
  clearAllModes: () => void;
  clearSkillState: () => void;
  cancelMovement: () => void;
  cancelAttack: () => void;
  cancelSkill: () => void;
  cancelItem: () => void;
  handleAuthError: () => void;
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  onExitBattle: () => void;
  isTutorialActiveRef: React.MutableRefObject<boolean>;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

export function useBattleActions({
  battleId, battle, selectedUnit, selectedSkill, selectedItem,
  setBattle, setSelectedUnit, setLoading, setError, setActionError,
  setShowVictoryScreen, setVictoryData,
  battleLog, setBattleLog, addToBattleLog, addEnhancedBattleLog,
  addFloatingText, spawnFx, processPassiveEvents, processAIActions, showTurnTransition,
  clearAllModes, clearSkillState, cancelMovement, cancelAttack, cancelSkill, cancelItem,
  handleAuthError, addNotification, onExitBattle, isTutorialActiveRef, confirm,
}: UseBattleActionsProps) {
  const [isProcessingTurn, setIsProcessingTurn] = useState(false);
  const isProcessingTurnRef = useRef(false);
  const [equippedSkills, setEquippedSkills] = useState<HotbarSkill[]>([]);
  const [battleItems, setBattleItems] = useState<BattleItem[]>([]);
  const [battleLogInitialized, setBattleLogInitialized] = useState(false);
  const [currentBattleName, setCurrentBattleName] = useState<string | null>(null);

  // Ref to break fetchBattleState ↔ endTurn circular dependency
  const endTurnRef = useRef<(unitOverride?: TurnOrderEntry) => Promise<void>>(async () => {});

  // Save battle state to localStorage on every relevant change
  useEffect(() => {
    if (battle && battleId) {
      const persistenceKey = `battle_${battleId}_state`;
      const battleState = {
        battle,
        battleLog,
        selectedUnit: selectedUnit ? { id: selectedUnit.id } : null,
        timestamp: Date.now()
      };
      try {
        localStorage.setItem(persistenceKey, JSON.stringify(battleState));
      } catch (err) {
        logger.warn('Failed to persist battle state:', err);
      }
    }
  }, [battle, battleLog, selectedUnit, battleId]);

  // Restore battle log from localStorage on mount (battle state comes from server)
  useEffect(() => {
    const persistenceKey = `battle_${battleId}_state`;
    try {
      const savedStateJson = localStorage.getItem(persistenceKey);
      if (savedStateJson) {
        const savedState = JSON.parse(savedStateJson);
        const timeDiff = Date.now() - (savedState.timestamp || 0);
        if (timeDiff < 30 * 60 * 1000 && savedState.battle) {
          if (savedState.battleLog && savedState.battleLog.length > 0) {
            setBattleLog(savedState.battleLog);
            setBattleLogInitialized(true);
            if (savedState.battle.battleName) {
              setCurrentBattleName(savedState.battle.battleName);
            }
          }
        } else {
          localStorage.removeItem(persistenceKey);
        }
      }
    } catch (err) {
      logger.warn('Failed to restore battle state:', err);
      localStorage.removeItem(persistenceKey);
    }
  }, [battleId, setBattleLog]);

  const fetchBattleState = useCallback(async (): Promise<void> => {
    try {
      const response = await makeAuthenticatedRequest(`/battles/${battleId}`);
      if (response.ok) {
        const data = await response.json();
        const battleData = (data.battleState || data) as BattleState;
        setBattle(battleData);

        if (battleData.battleName && (!battleLogInitialized || currentBattleName !== battleData.battleName)) {
          const battleStartExists = battleLog.some(
            entry => entry.includes('- The battle begins!') && entry.includes(battleData.battleName)
          );
          if (!battleStartExists) {
            addEnhancedBattleLog('battle_start', { battleName: battleData.battleName });
            setBattleLogInitialized(true);
            setCurrentBattleName(battleData.battleName);
          }
        }

        if (battleData.turnOrder && battleData.turnOrder.length > 0) {
          const currentIndex = battleData.currentUnitIndex || 0;
          const currentTurnUnit = battleData.turnOrder[currentIndex] || null;

          if (currentTurnUnit && currentTurnUnit.isPlayerUnit && battleData.status === 'InProgress') {
            const playerUnit = battleData.units?.find(
              unit => unit.isPlayerUnit && unit.id === currentTurnUnit.unitId
            );
            if (playerUnit && (!selectedUnit || selectedUnit.id !== playerUnit.id)) {
              setSelectedUnit(playerUnit);
              clearSkillState();
            }
          }

          if (currentTurnUnit && !currentTurnUnit.isPlayerUnit && battleData.status === 'InProgress' && !isProcessingTurnRef.current) {
            endTurnRef.current(currentTurnUnit).catch(err => {
              logger.error('Error calling endTurn for AI unit:', err);
            });
          }
        }
      } else {
        setError('Failed to load battle');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  // stale closure deps are intentional: battleLogInitialized/currentBattleName/battleLog/selectedUnit
  // avoid re-fetch loops by keeping dep array minimal
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battleId, handleAuthError]);

  const fetchEquippedSkills = useCallback(async (characterId: number) => {
    try {
      const response = await makeAuthenticatedRequest(`/skillhotbar/character/${characterId}`);
      if (response.ok) {
        setEquippedSkills(await response.json());
      } else {
        setEquippedSkills([]);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setEquippedSkills([]);
    }
  }, [handleAuthError]);

  useEffect(() => {
    if (selectedUnit?.isPlayerUnit && selectedUnit.characterId) {
      fetchEquippedSkills(selectedUnit.characterId).catch(err => {
        logger.error('Error fetching equipped skills:', err);
      });
    } else {
      setEquippedSkills([]);
    }
  }, [selectedUnit, fetchEquippedSkills]);

  // Fetch usable consumables from inventory, grouped by item name into stacks.
  // CraftedItems aren't stacked server-side (one row per craft), so quantity is a client-side count.
  const fetchBattleItems = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/crafted-items');
      if (!response.ok) { setBattleItems([]); return; }
      const items: Array<{ id: number; itemName: string; itemType: string; hpBonus: number }> = await response.json();
      const grouped = new Map<string, BattleItem>();
      for (const item of items) {
        if (item.itemType !== 'Consumable') continue;
        const isAntidote = item.itemName.toLowerCase().includes('antidote');
        if (item.hpBonus <= 0 && !isAntidote) continue; // e.g. Arrows - no in-battle effect
        const existing = grouped.get(item.itemName);
        if (existing) {
          existing.quantity += 1;
        } else {
          grouped.set(item.itemName, {
            craftedItemId: item.id,
            itemName: item.itemName,
            quantity: 1,
            healing: item.hpBonus,
            isAntidote,
          });
        }
      }
      setBattleItems(Array.from(grouped.values()));
    } catch (err) {
      logger.error('Error fetching battle items:', err);
      setBattleItems([]);
    }
  }, []);

  useEffect(() => {
    if (selectedUnit?.isPlayerUnit) {
      fetchBattleItems().catch(err => logger.error('Error fetching battle items:', err));
    }
  }, [selectedUnit, fetchBattleItems]);

  // Shared core for skill execution: sends the request and, on success, applies
  // battle state, log messages, floating text, FX, and the victory screen.
  // Returns the raw Response on failure so callers can apply their own error handling.
  const executeSkillCore = async (skill: HotbarSkill, targetX: number, targetY: number): Promise<Response | null> => {
    if (!skill || !selectedUnit) return null;
    const response = await makeAuthenticatedRequest(`/battles/${battleId}/use-skill`, {
      method: 'POST',
      body: JSON.stringify({ CasterId: selectedUnit.id, SkillNodeId: skill.skill.id, TargetX: targetX, TargetY: targetY })
    });
    if (!response.ok) return response;

    const result = await response.json();
    setBattle(result.battleState);
    result.battleMessages?.forEach((msg: string) => addToBattleLog(msg));
    const updatedUnit = (result.battleState as BattleState).units.find(u => u.id === selectedUnit?.id);
    setSelectedUnit(updatedUnit ?? null);
    const casterName = selectedUnit?.name ?? '';
    const targetUnit = (result.battleState as BattleState).units.find(
      (u: BattleUnitState) => result.skillEffect.affectedUnits?.includes(u.id)
    );
    const targetName = targetUnit?.name ?? '';
    const targetSuffix = targetName ? ` on ${targetName}` : '';
    const skillPixel = gridToPixel(targetX, targetY);
    if (result.skillEffect.damage > 0) {
      addFloatingText(`-${result.skillEffect.damage}`, 'damage', skillPixel.x, skillPixel.y);
      addToBattleLog(`⚔️ ${casterName} used ${result.skillEffect.skillName}${targetSuffix} dealing ${result.skillEffect.damage} damage!`);
    }
    if (result.skillEffect.healing > 0) {
      addFloatingText(`+${result.skillEffect.healing}`, 'healing', skillPixel.x, skillPixel.y);
      addToBattleLog(`✨ ${casterName} used ${result.skillEffect.skillName}${targetSuffix} healing ${result.skillEffect.healing} HP!`);
    }
    if (!result.skillEffect.damage && !result.skillEffect.healing) {
      addToBattleLog(`🎯 ${casterName} used ${result.skillEffect.skillName}!`);
    }
    if (result.skillEffect.cooldownApplied > 0) {
      addToBattleLog(`⏳ ${result.skillEffect.skillName} on cooldown for ${result.skillEffect.cooldownApplied} turns`);
    }
    // Spawn FX projectile for skill
    if (spawnFx && selectedUnit) {
      spawnFx(result.skillEffect.skillName ?? skill.skill.name ?? 'magic', selectedUnit.position, { x: targetX, y: targetY });
    }
    cancelSkill();
    if (result.battleState.status === 'Victory') {
      setVictoryData({
        experience: result.skillEffect.experience || 0,
        loot: result.skillEffect.materialsAwarded || [],
        tilesUnlocked: result.skillEffect.tilesUnlocked || [],
        bonuses: result.skillEffect.bonuses || null,
        characterXP: result.skillEffect.characterXP || [],
      });
      setShowVictoryScreen(true);
    }
    return response;
  };

  const executeSkillDirect = async (skill: HotbarSkill, targetX: number, targetY: number) => {
    try {
      const response = await executeSkillCore(skill, targetX, targetY);
      if (response && !response.ok) {
        addToBattleLog(`❌ Failed to use ${skill.skill.name}: ${await response.text()}`);
      }
    } catch (err) {
      addToBattleLog(`❌ Error using skill`);
    }
  };

  const executeSkill = async (targetX: number, targetY: number) => {
    if (!selectedSkill) return;
    try {
      const response = await executeSkillCore(selectedSkill, targetX, targetY);
      if (response && !response.ok) {
        const err = await response.json();
        setActionError(err.error || 'Failed to use skill');
        logger.error('Failed to use skill:', err);
      }
    } catch (err) {
      logger.error('Error using skill:', err);
      setActionError('An error occurred while using the skill');
    }
  };

  const consumeItem = async (targetUnitId: number) => {
    if (!selectedItem || !selectedUnit) return;
    try {
      const response = await makeAuthenticatedRequest(`/battles/${battleId}/use-item`, {
        method: 'POST',
        body: JSON.stringify({ CasterId: selectedUnit.id, CraftedItemId: selectedItem.craftedItemId, TargetUnitId: targetUnitId })
      });
      if (response.ok) {
        const result = await response.json();
        setBattle(result.battleState);
        result.battleMessages?.forEach((msg: string) => addToBattleLog(msg));
        const updatedUnit = (result.battleState as BattleState).units.find(u => u.id === selectedUnit?.id);
        setSelectedUnit(updatedUnit ?? null);

        const targetUnit = (result.battleState as BattleState).units.find(u => u.id === result.itemEffect.affectedUnitId);
        if (targetUnit) {
          const pixel = gridToPixel(targetUnit.position.x, targetUnit.position.y);
          if (result.itemEffect.healing > 0) {
            addFloatingText(`+${result.itemEffect.healing}`, 'healing', pixel.x, pixel.y);
          }
          if (result.itemEffect.curedEffects?.length > 0) {
            addFloatingText(`✨ Cured`, 'status', pixel.x, pixel.y - 20);
          }
        }
        cancelItem();
        fetchBattleItems().catch(err => logger.error('Error refreshing battle items:', err));
      } else {
        const err = await response.json();
        setActionError(err.error || 'Failed to use item');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setActionError(apiError.message);
    }
  };

  const moveUnit = async (unitId: number, targetX: number, targetY: number) => {
    try {
      const response = await makeAuthenticatedRequest(`/battles/${battleId}/move`, {
        method: 'POST',
        body: JSON.stringify({ UnitId: unitId, TargetX: targetX, TargetY: targetY })
      });
      if (response.ok) {
        const result = await response.json();

        result.opportunityAttacks?.forEach((oa: OpportunityAttack) => {
          if (oa.type === 'Grapple') {
            if (oa.grappleSuccess) {
              addToBattleLog(`💥 ${oa.message}`);
              addNotification({ type: 'info', title: 'Grappled!', message: `${oa.attackerName} grappled your unit!` });
            } else {
              addToBattleLog(`🏃 ${oa.message}`);
            }
          } else {
            addToBattleLog(`⚔️ ${oa.message}`);
            if ((oa.damage ?? 0) > 0) {
              addNotification({ type: 'info', title: 'Opportunity Attack', message: `${oa.attackerName} dealt ${oa.damage ?? 0} damage!` });
            }
          }
        });

        if (result.grappled) {
          addNotification({ type: 'info', title: 'Movement Stopped', message: 'Your unit was grappled!' });
          if (result.autoEndTurn) {
            addToBattleLog(`Turn ended automatically due to grapple`);
            const currentUnit = getCurrentTurnUnit(battle);
            if (currentUnit) {
              setTimeout(() => {
                endTurnRef.current(currentUnit).catch(err => {
                  logger.error('Error auto-ending turn after grapple:', err);
                });
              }, 1000);
            }
          }
        }

        const updatedBattle = (result.battleState || result) as BattleState;
        setBattle(updatedBattle);

        if (result.unitDied) {
          const dyingUnit = selectedUnit;
          setSelectedUnit(null);
          cancelMovement();
          setActionError(null);
          addToBattleLog(`💀 ${dyingUnit?.name} was killed by opportunity attack!`);
          addNotification({ type: 'info', title: 'Unit Killed', message: `${dyingUnit?.name} died from opportunity attack!` });
          return;
        }

        const updatedUnit = updatedBattle.units.find((u: BattleUnitState) => u.id === unitId);
        setSelectedUnit(updatedUnit ?? null);
        setActionError(null);
        if (updatedUnit) {
          const distance = Math.abs(targetX - (selectedUnit?.position.x ?? targetX)) + Math.abs(targetY - (selectedUnit?.position.y ?? targetY));
          addEnhancedBattleLog('unit_move', { unitName: updatedUnit.name, distance });
        }
        cancelMovement();
      } else {
        const errorData = await response.json();
        setActionError(errorData.error || 'Failed to move unit');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setActionError(apiError.message);
    }
  };

  const attackUnit = async (attackerId: number, targetId: number) => {
    try {
      const response = await makeAuthenticatedRequest(`/battles/${battleId}/attack`, {
        method: 'POST',
        body: JSON.stringify({ AttackerId: attackerId, TargetId: targetId })
      });
      if (response.ok) {
        const result = await response.json();
        if (result.passiveEvents?.length > 0) {
          processPassiveEvents(result.passiveEvents, result.battleState?.units ?? [], addEnhancedBattleLog);
        }
        setBattle(result.battleState);
        cancelAttack();
        const updatedUnit = (result.battleState as BattleState).units.find(u => u.id === attackerId);
        setSelectedUnit(updatedUnit ?? null);
        setActionError(null);

        const { unitsHit, battleStatus, xpGained, materialsAwarded } = result.attackResult;
        const primaryTargetHit = (unitsHit as Array<{ unitId: number; damage: number; defeated: boolean; unitName?: string; isCritical?: boolean; missed?: boolean; comboType?: string; comboDescription?: string }>)
          ?.find(hit => hit.unitId === targetId);
        const damage = primaryTargetHit?.damage || 0;
        const isCritical = primaryTargetHit?.isCritical ?? false;
        const missed = primaryTargetHit?.missed ?? false;
        const targetDefeated = primaryTargetHit?.defeated || false;
        const comboType = primaryTargetHit?.comboType;
        const comboDescription = primaryTargetHit?.comboDescription;
        const targetUnit = battle?.units.find(u => u.id === targetId);
        const attackerUnit = battle?.units.find(u => u.id === attackerId);
        const targetName = targetUnit?.name || primaryTargetHit?.unitName || 'Unknown';
        const targetRemainingHP = targetDefeated ? 0 : (targetUnit?.stats.currentHP ?? 0);
        const targetMaxHP = targetUnit?.stats.maxHP ?? 100;

        addEnhancedBattleLog('attack', {
          attackerName: updatedUnit?.name,
          targetName,
          damage,
          targetDefeated,
          targetRemainingHP,
          targetMaxHP,
          comboDescription,
        });

        // Floating text + FX for player attack
        if (targetUnit) {
          const tx = targetUnit.position.x;
          const ty = targetUnit.position.y;
          const targetPixel = gridToPixel(tx, ty);
          if (comboType) {
            addFloatingText(getComboCallout(comboType), 'combo', targetPixel.x, targetPixel.y - 30);
          }
          if (missed) {
            addFloatingText('MISS', 'status', targetPixel.x, targetPixel.y);
          } else if (isCritical) {
            addFloatingText(`💥 CRIT -${damage}`, 'critical', targetPixel.x, targetPixel.y);
          } else if (damage > 0) {
            addFloatingText(`-${damage}`, 'damage', targetPixel.x, targetPixel.y);
          }
          if (targetDefeated) addFloatingText('💀', 'status', targetPixel.x, targetPixel.y);

          // Spawn FX (melee = slash, ranged = arrow based on distance)
          if (spawnFx && attackerUnit) {
            const dist = Math.abs(attackerUnit.position.x - tx) + Math.abs(attackerUnit.position.y - ty);
            spawnFx(dist > 1 ? 'arrow' : 'slash', attackerUnit.position, targetUnit.position);
          }
        }

        if (xpGained && xpGained >= 10) {
          addEnhancedBattleLog('xp_gain', { unitName: updatedUnit?.name, xpAmount: xpGained });
        }

        if (battleStatus === 'Victory') {
          addEnhancedBattleLog('victory', {});
          result.attackResult.achievementsUnlocked?.forEach((achievement: AchievementUnlock) => {
            addNotification({
              type: 'achievement',
              title: `${achievement.icon} Achievement Unlocked!`,
              message: `${achievement.name} - ${achievement.description} (${achievement.currentProgress}/${achievement.requiredValue})`,
            });
          });
          setVictoryData({
            experience: xpGained || 0,
            loot: materialsAwarded || [],
            tilesUnlocked: result.attackResult.tilesUnlocked || [],
            bonuses: result.attackResult.bonuses || null,
            characterXP: result.attackResult.characterXP || [],
          });
          setShowVictoryScreen(true);
          if (materialsAwarded?.length > 0) {
            addEnhancedBattleLog('materials_found', { materials: materialsAwarded });
            materialsAwarded.forEach((material: MaterialAward) => {
              const rarity = material.rarity || 'Common';
              if (rarity === 'Rare' || rarity === 'Epic' || rarity === 'Legendary') {
                addNotification({ type: 'loot', title: `${rarity} Loot Found!`, message: `You found ${material.quantity}x ${material.materialName}!` });
              }
            });
          }
          if (result.attackResult.recipesDiscovered?.length > 0) {
            addEnhancedBattleLog('recipes_discovered', { recipes: result.attackResult.recipesDiscovered });
            result.attackResult.recipesDiscovered.forEach((recipe: DiscoveredRecipe) => {
              addNotification({ type: 'quest', title: 'New Recipe Discovered!', message: `You learned how to craft: ${recipe.recipeName}` });
            });
          }
        } else if (battleStatus === 'Defeat') {
          addEnhancedBattleLog('defeat', {});
        }
      } else {
        const errorData = await response.json();
        setActionError(errorData.details || errorData.error || 'Failed to attack');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setActionError(apiError.message);
    }
  };

  const waitForTutorial = (callback: () => void) => {
    if (!isTutorialActiveRef.current) { callback(); return; }
    const interval = setInterval(() => {
      if (!isTutorialActiveRef.current) { clearInterval(interval); callback(); }
    }, 250);
  };

  const endTurn = async (unitOverride?: TurnOrderEntry) => {
    if (isProcessingTurn) return;
    isProcessingTurnRef.current = true;
    setIsProcessingTurn(true);
    clearAllModes();

    const currentTurnUnit = unitOverride || getCurrentTurnUnit(battle);
    if (!currentTurnUnit) {
      logger.warn('No current unit found in endTurn, but proceeding anyway for AI processing');
    }

    try {
      const response = await makeAuthenticatedRequest(`/battles/${battleId}/end-turn`, { method: 'POST' });
      if (response.ok) {
        const result = await response.json();
        result.battleMessages?.forEach((msg: string) => addToBattleLog(msg));

        if (result.aiActions && result.aiActions.length > 0) {
          const finalBattleState = result.battleState;
          setSelectedUnit(null);

          const nextTurnUnit = result.battleState?.turnOrder?.[result.battleState.currentUnitIndex];

          waitForTutorial(() => setTimeout(() => {
            processAIActions(result.aiActions, () => {
              // Announce next turn after all AI animations finish
              if (nextTurnUnit) {
                showTurnTransition(nextTurnUnit.name, nextTurnUnit.isPlayerUnit);
                addEnhancedBattleLog('turn_start', {
                  unitName: nextTurnUnit.name,
                  isPlayerUnit: nextTurnUnit.isPlayerUnit,
                  turnNumber: result.battleState.currentTurn,
                });
              }
              setBattle(finalBattleState);
              setTimeout(() => {
                fetchBattleState().then(() => {
                  const ctu = getCurrentTurnUnit(battle);
                  if (ctu && !ctu.isPlayerUnit && battle?.status === 'InProgress' && !isProcessingTurn) {
                    waitForTutorial(() => endTurnRef.current(ctu));
                  }
                });
              }, 1000);
            });
          }, 500));
        } else {
          const nextTurnUnit = result.battleState?.turnOrder?.[result.battleState?.currentUnitIndex];
          if (nextTurnUnit) {
            showTurnTransition(nextTurnUnit.name, nextTurnUnit.isPlayerUnit);
            addEnhancedBattleLog('turn_start', {
              unitName: nextTurnUnit.name,
              isPlayerUnit: nextTurnUnit.isPlayerUnit,
              turnNumber: result.battleState.currentTurn,
            });
          }
          setBattle(result.battleState);
          setSelectedUnit(null);
          setTimeout(() => {
            fetchBattleState().then(() => {
              const ctu = getCurrentTurnUnit(battle);
              if (ctu && !ctu.isPlayerUnit && battle?.status === 'InProgress' && !isProcessingTurn) {
                waitForTutorial(() => endTurnRef.current(ctu));
              }
            });
          }, 500);
        }
      } else {
        const errorData = await response.json();
        logger.error('End turn failed:', response.status, errorData);
        setError(errorData.error || 'Failed to end turn');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      isProcessingTurnRef.current = false;
      setIsProcessingTurn(false);
    }
  };
  endTurnRef.current = endTurn;

  const handleFleeBattle = async () => {
    try {
      if (!battle || battle.status !== 'InProgress') {
        onExitBattle();
        return;
      }
      const playerUnits = battle.units?.filter(u => u.isPlayerUnit && u.characterId) || [];
      if (playerUnits.length === 0) {
        onExitBattle();
        return;
      }

      let totalPenalty = 0;
      const penaltyList: string[] = [];
      for (const unit of playerUnits) {
        if (unit.character) {
          const currentXP = (unit.character.experience as number) || 0;
          const penalty = Math.ceil(currentXP * 0.01);
          totalPenalty += penalty;
          penaltyList.push(`${unit.name}: -${penalty} XP`);
        }
      }

      const confirmed = await confirm({
        title: 'Flee Battle?',
        message: `Fleeing will cost you ${totalPenalty} XP total:\n\n${penaltyList.join('\n')}\n\nAre you sure you want to flee?`,
        confirmText: 'Flee',
        danger: true,
      });
      if (!confirmed) return;

      const response = await makeAuthenticatedRequest(`/battles/${battleId}/flee`, { method: 'POST' });
      if (response.ok) {
        const result = await response.json();
        addNotification({ type: 'info', title: 'Fled Battle', message: `You fled the battle. Total XP penalty: ${result.totalPenalty}` });
        onExitBattle();
      } else {
        const errorData = await response.json();
        addNotification({ type: 'info', title: 'Flee Failed', message: errorData.error || 'Failed to flee battle' });
      }
    } catch (err) {
      logger.error('Error fleeing battle:', err);
      addNotification({ type: 'info', title: 'Error', message: 'An error occurred while fleeing the battle' });
    }
  };

  return {
    fetchBattleState,
    moveUnit,
    attackUnit,
    executeSkill,
    executeSkillDirect,
    consumeItem,
    endTurn,
    handleFleeBattle,
    equippedSkills,
    battleItems,
  };
}
